///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Builds the time control's view model from redux, the entitlement tier
/// and the engine's layer manager. Owns the polling of lane availability,
/// the anchored playback window and the window-relative keyboard
/// shortcuts, so the rows that render it hold no time logic of their own.

import { type TimeLane } from "@/earthbrowser/datasets/TimeLane"
import { useEntitlements } from "@/hooks/useEntitlements"
import { useLayerLoadStates } from "@/hooks/useLayerLoadStates"
import { isEditableTarget } from "@/hooks/useTimeCameraShortcuts"
import { setAuthOpen, setSubscribeOpen } from "@/redux/dialogSlice"
import {
	setClockAnimatingSpeed,
	setClockCurrentTime,
	setClockEndOffset,
	setClockStartOffset,
	setClockState,
	setTimeZoneMode,
	toggleTimeControlExpanded,
} from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import {
	adjacentFrameTime,
	cHourMs,
	formatWeekday,
	midnightsBetween,
	playbackWindow,
	stepOffsetHours,
} from "@/utils/timeControl"
import { useEffect, useMemo, useState } from "react"
import {
	cLaneMeta,
	cMaxOffsetHours,
	stripPct,
	type TimeControlActions,
	type TimeControlModel,
	type TimeLaneView,
} from "./timeControlModel"

// Loaded spans change while frames stream in, so poll a little faster than
// the old 5s when the lanes are on screen; faster still while a lane is
// waiting on the clock's frame, so the lane shimmer tracks the load
const cRangePollMs = 1500
const cRangePollWaitingMs = 500

export function useTimeControlModel(): TimeControlModel {
	const dispatch = useAppDispatch()
	const clockState = useAppSelector(state => state.earthbrowser.clockState)
	const clockAnimatingSpeed = useAppSelector(state => state.earthbrowser.clockAnimatingSpeed)
	const clockCurrentTime = useAppSelector(state => state.earthbrowser.clockCurrentTime)
	const clockStartOffset = useAppSelector(state => state.earthbrowser.clockStartOffset)
	const clockEndOffset = useAppSelector(state => state.earthbrowser.clockEndOffset)
	const timeZoneMode = useAppSelector(state => state.earthbrowser.timeZoneMode)
	const isExpanded = useAppSelector(state => state.earthbrowser.timeControlExpanded)
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)
	const speedCap = useAppSelector(state => state.dialog.clockSpeedCap)
	const userProfile = useAppSelector(state => state.user.userProfile)

	// Phase 4 depth gate: free tier scrubs 2 hours of history, paid 48
	// (SUBSCRIPTION_PRICING_MODEL.md). The persisted clockStartOffset is
	// clamped at read time so a lapsed subscriber's stored 48h window
	// degrades gracefully without a migration.
	const { historyHours: entitledHistoryHours, isPaid } = useEntitlements()
	const historyHours = Math.min(clockStartOffset, entitledHistoryHours)
	const maxHistoryHours = Math.min(cMaxOffsetHours, entitledHistoryHours)

	// Effective playback speed: the focused entity (e.g. a tracked satellite)
	// can cap the clock without touching the user's stored setting
	const effectiveSpeed = speedCap !== null ? Math.min(clockAnimatingSpeed, speedCap) : clockAnimatingSpeed
	const isSpeedCapped = speedCap !== null && clockAnimatingSpeed > speedCap

	// The window's "now" anchor. It tracks wall-clock time while the clock
	// runs but freezes while paused, so a paused playhead doesn't drift left
	// (and eventually off the strip) as real time marches on. Mirrors the
	// engine Clock, which only recomputes its range while not paused.
	const [nowMs, setNowMs] = useState(() => Date.now())
	useEffect(() => {
		if (clockState !== "paused") {
			setNowMs(Date.now())
		}
	}, [clockState, clockCurrentTime])

	const { startMs, endMs } = playbackWindow(nowMs, historyHours, clockEndOffset)
	const spanMs = Math.max(1, endMs - startMs)
	const strip = { startMs, spanMs }

	// Time-series layers whose frame for the clock's time isn't loaded yet
	// (after the shared show-delay hysteresis): these lanes shimmer
	const loadStates = useLayerLoadStates()
	const waitingLaneIds = useMemo(
		() => new Set(loadStates.filter(s => s.visible && s.phase === "frames" && !s.done && cLaneMeta[s.id]).map(s => s.id)),
		[loadStates]
	)
	const anyLaneWaiting = waitingLaneIds.size > 0

	// Availability lanes of the visible time-based datasets plus the frame
	// times used for stepping. Ranges only change on layer sync/refresh and
	// loaded spans as frames stream in, so a slow poll of the engine is
	// enough; re-polling on visibleDatasets keeps layer toggles snappy.
	const [lanesState, setLanesState] = useState<{ lanes: TimeLane[]; frameTimes: number[] }>({ lanes: [], frameTimes: [] })
	useEffect(() => {
		if (!layerManager) return
		const update = () => setLanesState({
			lanes: layerManager.getVisibleTimeRanges(),
			frameTimes: layerManager.getPrimaryFrameTimes(),
		})
		update()
		const interval = setInterval(update, anyLaneWaiting ? cRangePollWaitingMs : cRangePollMs)
		return () => clearInterval(interval)
	}, [layerManager, visibleDatasets, anyLaneWaiting])

	// Lanes for datasets we have metadata for, in a stable order
	const rawLanes = useMemo(
		() => lanesState.lanes.filter(r => cLaneMeta[r.id]).sort((a, b) => a.id.localeCompare(b.id)),
		[lanesState.lanes]
	)
	const lanes: TimeLaneView[] = rawLanes.map(lane => {
		const meta = cLaneMeta[lane.id]
		return {
			id: lane.id,
			label: meta.label,
			color: meta.color,
			startPct: stripPct(strip, lane.startMs),
			endPct: stripPct(strip, lane.endMs),
			loaded: lane.loadedRanges
				.map(seg => ({
					startPct: stripPct(strip, Math.max(seg.startMs, lane.startMs)),
					endPct: stripPct(strip, Math.min(seg.endMs, lane.endMs)),
				}))
				.filter(seg => seg.endPct - seg.startPct >= 0.3),
			waiting: waitingLaneIds.has(lane.id),
			startMs: lane.startMs,
			endMs: lane.endMs,
			loadedRanges: lane.loadedRanges,
		}
	})

	// Frame stepping on the primary visible frame-based layer. Layers can
	// expose frame times reaching further back than the entitled history
	// depth (only radar/MRMS cap their frame lists by tier), so filter to
	// the entitlement floor or the step buttons walk past the license gate.
	const entitledStartMs = nowMs - entitledHistoryHours * cHourMs
	const stepFrameTimes = lanesState.frameTimes.filter(t => t >= entitledStartMs)
	const prevFrameMs = adjacentFrameTime(stepFrameTimes, clockCurrentTime, -1)
	const nextFrameMs = adjacentFrameTime(stepFrameTimes, clockCurrentTime, 1)
	const frameCount = stepFrameTimes.filter(t => t >= startMs && t <= endMs).length

	// Midnight ticks orient the user across multi-day windows
	const midnights = midnightsBetween(startMs, endMs, timeZoneMode).map(ms => ({
		ms,
		pct: stripPct(strip, ms),
		label: formatWeekday(ms, timeZoneMode),
	}))

	const pause = () => {
		if (clockState !== "paused") {
			dispatch(setClockState("paused"))
		}
	}

	const jumpTo = (ms: number) => {
		pause()
		dispatch(setClockCurrentTime(ms))
	}

	// Checkout needs an account: signed-out users register first
	const openUpsell = () => {
		dispatch(userProfile ? setSubscribeOpen(true) : setAuthOpen(true))
	}

	// Adjust hours of history (start) or forecast (end) around now.
	// History is capped by the subscription tier; stepping past the free
	// cap opens the subscribe flow instead (future offsets stay ungated).
	const adjustOffset = (isStart: boolean, direction: 1 | -1) => {
		const maxHours = isStart ? maxHistoryHours : cMaxOffsetHours
		const current = isStart ? historyHours : clockEndOffset
		if (isStart && direction > 0 && current >= maxHours && maxHours < cMaxOffsetHours) {
			openUpsell()
			return
		}
		const next = stepOffsetHours(current, direction, isStart ? 1 : 0, maxHours)
		dispatch(isStart ? setClockStartOffset(next) : setClockEndOffset(next))
	}

	const actions: TimeControlActions = {
		// Play/pause only concerns animation; LIVE is its own mode toggle.
		// From paused, play starts animating from the displayed time; from
		// live the engine restarts the loop at the history-window start
		// (Clock.ts) so playback covers the observed data, not the forecast
		togglePlay: () => {
			dispatch(setClockState(clockState === "animating" ? "paused" : "animating"))
		},
		toggleLive: () => {
			if (clockState === "real-time") {
				dispatch(setClockState("paused"))
			} else {
				dispatch(setClockCurrentTime(Date.now()))
				dispatch(setClockState("real-time"))
			}
		},
		setSpeed: (speed: number) => {
			dispatch(setClockAnimatingSpeed(speed))
			if (clockState === "real-time") {
				dispatch(setClockState("animating"))
			}
		},
		scrubToFraction: (fraction: number) => {
			jumpTo(startMs + spanMs * Math.max(0, Math.min(1, fraction)))
		},
		jumpTo,
		stepFrame: (direction: 1 | -1) => {
			const ms = direction > 0 ? nextFrameMs : prevFrameMs
			if (ms !== null) jumpTo(ms)
		},
		adjustHistory: direction => adjustOffset(true, direction),
		adjustForecast: direction => adjustOffset(false, direction),
		setHistoryHours: (hours: number) => {
			if (hours > maxHistoryHours) {
				openUpsell()
				return
			}
			dispatch(setClockStartOffset(hours))
		},
		setForecastHours: (hours: number) => {
			dispatch(setClockEndOffset(Math.min(cMaxOffsetHours, hours)))
		},
		toggleExpanded: () => dispatch(toggleTimeControlExpanded()),
		setTimeZoneMode: mode => dispatch(setTimeZoneMode(mode)),
		openUpsell,
	}

	// Window-relative shortcuts live here (not in useTimeCameraShortcuts)
	// because they need the anchored window and the primary frame times
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.metaKey || e.ctrlKey || e.altKey || isEditableTarget(e.target)) return
			switch (e.key) {
				case "Home":
					e.preventDefault()
					jumpTo(startMs)
					break
				case "End":
					e.preventDefault()
					jumpTo(endMs)
					break
				case ",":
					e.preventDefault()
					actions.stepFrame(-1)
					break
				case ".":
					e.preventDefault()
					actions.stepFrame(1)
					break
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	})

	return {
		clockState,
		isLive: clockState === "real-time",
		isAnimating: clockState === "animating",
		isExpanded,
		timeZoneMode,
		nowMs,
		currentMs: clockCurrentTime,
		startMs,
		endMs,
		spanMs,
		nowPct: stripPct(strip, nowMs),
		playheadPct: stripPct(strip, clockCurrentTime),
		// Live is at now by definition, even if the engine clock runs a
		// beat ahead of the anchor with no forecast depth
		beforeWindow: clockState !== "real-time" && clockCurrentTime < startMs - 1000,
		afterWindow: clockState !== "real-time" && clockCurrentTime > endMs + 1000,
		offsetFromNowMs: clockCurrentTime - nowMs,
		speed: {
			configured: clockAnimatingSpeed,
			effective: effectiveSpeed,
			capped: isSpeedCapped,
			cap: speedCap,
		},
		window: {
			historyHours,
			forecastHours: clockEndOffset,
			maxHistoryHours,
			maxHours: cMaxOffsetHours,
			isPaid,
			frameCount,
		},
		lanes,
		midnights,
		frames: { hasFrames: stepFrameTimes.length > 1, prevMs: prevFrameMs, nextMs: nextFrameMs },
		actions,
	}
}
